'use client';

import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { Button } from '../../../components/button';
import { Collapsible } from '../../../components/collapsible';
import { MetaDefault, MonoSmall } from '../../../components/typography';
import { DataMessageCard } from '../components/DataMessageCard';
import type { ChatObjectNavigationTarget } from '../components/hitl-types';
import { MessageSkeletonCard } from '../components/MessageSkeletonCard';
import { ThinkingBlock } from '../components/ThinkingBlock';
import { Box, HStack } from '@terra/ui-styled-system';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import { useState } from 'react';
import type {
  SubagentActivityItem,
  SubagentOpenTarget,
} from './subagent-activity';

const ACTIVITY_SHELL_MESSAGE: TerraUIMessage = {
  id: 'subagent-activity-shell',
  role: 'assistant',
  parts: [],
};

export function SubagentActivityLine({
  item,
  expansionKey,
  onOpenActivity,
  onOpenObject,
}: {
  item: SubagentActivityItem;
  expansionKey: string;
  onOpenActivity?: (target: SubagentOpenTarget) => void;
  onOpenObject?: (target: ChatObjectNavigationTarget) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (item.kind === 'thought') {
    const timing = item.providerMetadata?.metadata;

    return (
      <ThinkingBlock
        expansionKey={expansionKey}
        message={{
          type: 'reasoning',
          text: item.text,
          state: item.isStreaming ? 'streaming' : 'done',
          ...(timing &&
          (typeof timing.reasoningStartedAt === 'number' ||
            typeof timing.reasoningEndedAt === 'number')
            ? {
                providerMetadata: {
                  metadata: {
                    ...(typeof timing.reasoningStartedAt === 'number'
                      ? { reasoningStartedAt: timing.reasoningStartedAt }
                      : {}),
                    ...(typeof timing.reasoningEndedAt === 'number'
                      ? { reasoningEndedAt: timing.reasoningEndedAt }
                      : {}),
                  },
                },
              }
            : {}),
        }}
      />
    );
  }

  if (item.kind === 'text') {
    return (
      <Box
        width="full"
        minW={0}
        data-testid="subagent-activity-text"
        data-streaming={item.isStreaming || undefined}
      >
        <MessageSkeletonCard type="assistant" markdownSource={item.text} />
      </Box>
    );
  }

  const isRunning = item.status === 'started' || item.status === 'in-progress';
  const label = isRunning
    ? item.labels.labelPresent
    : item.status === 'error'
      ? item.labels.labelFailed
      : item.labels.labelPast;
  const summary = item.target || item.detail;

  return (
    <Collapsible.Root
      open={isExpanded}
      onOpenChange={({ open }) => setIsExpanded(open)}
      data-testid="subagent-activity-tool"
      data-expanded={isExpanded ? 'true' : 'false'}
    >
      <Collapsible.Trigger asChild>
        <HStack
          gap={2}
          alignItems="center"
          width="full"
          minW={0}
          minH="7"
          cursor="pointer"
          color="fg.muted"
          _hover={{ color: 'fg.default' }}
        >
          <MetaDefault
            truncate
            flexShrink={0}
            color="fg.muted"
            animation={
              isRunning ? 'shimmer-opacity 2s ease-in-out infinite' : undefined
            }
            _motionReduce={{ animation: 'none' }}
          >
            {label}
          </MetaDefault>
          {summary && !isExpanded ? (
            <MonoSmall truncate color="fg.subtle" minW={0}>
              {summary}
            </MonoSmall>
          ) : null}
          <Box display="flex" alignItems="center" flexShrink={0} ml="auto">
            {isExpanded ? (
              <ArrowDown01Icon size={10} />
            ) : (
              <ArrowRight01Icon size={10} />
            )}
          </Box>
        </HStack>
      </Collapsible.Trigger>
      {isExpanded ? (
        <Box pt={2} width="full" minW={0}>
          {item.openTarget && onOpenActivity ? (
            <Button
              variant="link"
              size="xs"
              maxW="full"
              mb={2}
              data-testid="subagent-activity-open"
              onClick={() => {
                if (item.openTarget) {
                  onOpenActivity(item.openTarget);
                }
              }}
            >
              <MonoSmall truncate>{item.openTarget.path}</MonoSmall>
            </Button>
          ) : null}
          <DataMessageCard
            uiMessage={ACTIVITY_SHELL_MESSAGE}
            dataMessage={item.dataMessage}
            forceExpandedToolCall
            revealHiddenOutput
            onOpenObject={onOpenObject}
          />
        </Box>
      ) : null}
    </Collapsible.Root>
  );
}
